import { AxiosError } from "axios";

/**
 * API 에러에서 사용자 친화적인 메시지를 추출하는 함수
 * 
 * @param error - AxiosError 또는 일반 Error
 * @returns 사용자에게 표시할 에러 메시지
 */
export const getErrorMessage = (error: unknown): string => {
  // AxiosError인 경우
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<{
      statusCode?: number;
      message?: string;
      error?: string;
    }>;

    const status = axiosError.response?.status;
    const responseData = axiosError.response?.data;

    // 403 Forbidden: 권한 없음
    if (status === 403) {
      // 백엔드에서 제공한 메시지가 있으면 사용
      if (responseData?.message) {
        return responseData.message;
      }
      if (responseData?.error) {
        return responseData.error;
      }
      // 기본 메시지
      return "이 작업을 수행할 권한이 없습니다.";
    }

    // 401 Unauthorized: 인증 실패
    if (status === 401) {
      if (responseData?.message) {
        return responseData.message;
      }
      if (responseData?.error) {
        return responseData.error;
      }
      return "인증이 필요합니다. 다시 로그인해주세요.";
    }

    // 404 Not Found
    if (status === 404) {
      if (responseData?.message) {
        return responseData.message;
      }
      return "요청한 리소스를 찾을 수 없습니다.";
    }

    // 500 Internal Server Error
    if (status === 500) {
      if (responseData?.message) {
        return responseData.message;
      }
      return "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
    }

    // 기타 HTTP 에러
    if (status) {
      if (responseData?.message) {
        return responseData.message;
      }
      if (responseData?.error) {
        return responseData.error;
      }
      return `요청 처리 중 오류가 발생했습니다. (${status})`;
    }

    // 네트워크 에러
    if (axiosError.request && !axiosError.response) {
      return "서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.";
    }
  }

  // 일반 Error인 경우
  if (error instanceof Error) {
    return error.message;
  }

  // 알 수 없는 에러
  return "알 수 없는 오류가 발생했습니다.";
};

/**
 * API 에러가 권한 관련 에러인지 확인하는 함수
 * 
 * @param error - AxiosError 또는 일반 Error
 * @returns 권한 관련 에러인지 여부
 */
export const isForbiddenError = (error: unknown): boolean => {
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 403;
  }
  return false;
};

/**
 * API 에러가 인증 관련 에러인지 확인하는 함수
 * 
 * @param error - AxiosError 또는 일반 Error
 * @returns 인증 관련 에러인지 여부
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError;
    return axiosError.response?.status === 401;
  }
  return false;
};


